import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import CutoutButton from './CutoutButton';

/**
 * WhyChooseUs - Shared component for homepage and About Us page
 * @param {string} sectionId - data-section attribute value (default: 'why-choose-us')
 * @param {boolean} minHeight - Whether to use min-h-screen (default: false for homepage)
 */
const WhyChooseUs = ({ sectionId = 'why-choose-us-home', minHeight = false }) => {
    const { t, i18n } = useTranslation('about');
    const navigate = useNavigate();
    const { lang = 'en' } = useParams();

    const isHome = sectionId === 'why-choose-us-home';

    // Determine read more label based on current language
    const readMoreLabel = i18n.language === 'da' ? 'Læs mere' : 'Read more';

    const whyChooseUsKeys = [
        'experiencedTeam',
        'clientPartnership',
        'modernTech',
        'aiAugmentation',
        'customCrm',
        'ongoingSupport',
    ];

    const whyChooseUsIcons = [
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>,
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138z" /></svg>,
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h12M3 17h6M21 7v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7" /></svg>,
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
    ];

    return (
        <section
            className={`bg-black py-20 pb-12 lg:py-32 lg:pb-40 ${minHeight ? 'min-h-screen' : ''} flex items-center`}
            data-section={sectionId}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="flex flex-col md:flex-row justify-between items-start mb-16"
                >
                    <div>
                        <p className="text-white/60 text-sm font-semibold mb-2 uppercase tracking-wider flex items-center gap-2">
                            <span className="w-2 h-2 bg-white/60 rounded-full"></span>
                            {t('whyChooseUs.badge')}
                        </p>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                            {t('whyChooseUs.title')}
                        </h2>
                    </div>
                    <div className="text-left md:text-right max-w-md mt-4 md:mt-0">
                        <p className="text-base sm:text-lg text-white/70">
                            {t('whyChooseUs.description')}
                        </p>
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {whyChooseUsKeys.map((key, index) => {
                        // On homepage mobile, hide items after index 2 (so only 0, 1, 2 show) unless expanded
                        const isHidden = isHome && index >= 3;

                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className={`group cursor-pointer ${isHidden ? 'hidden lg:block' : 'block'}`}
                            >
                                <div className="bg-[#111111] rounded-2xl p-8 group-hover:bg-[#1a1a1a] transition-all duration-300 relative overflow-hidden aspect-square flex flex-col border border-white/5 hover:border-white/10">
                                    {/* Icon - Top Left */}
                                    <div className="absolute top-6 left-6">
                                        <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center text-white group-hover:bg-white/15 transition-all duration-300">
                                            {whyChooseUsIcons[index]}
                                        </div>
                                    </div>

                                    {/* Arrow Icon - Top Right */}
                                    <div className="absolute top-6 right-6">
                                        <div className="w-10 h-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            <svg className="w-6 h-6 text-white transform -rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                        </div>
                                    </div>

                                    {/* Spacer */}
                                    <div className="flex-grow"></div>

                                    {/* Content - Bottom */}
                                    <div>
                                        <h3 className="text-2xl font-bold text-white mb-4 transition-colors duration-300">
                                            {t(`whyChooseUs.items.${key}.title`)}
                                        </h3>
                                        <p className="text-white/50 text-base leading-relaxed transition-colors duration-300">
                                            {t(`whyChooseUs.items.${key}.description`)}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Read More Button for Mobile Home */}
                {isHome && (
                    <div className="flex justify-center mt-8 lg:hidden">
                        <CutoutButton
                            onClick={() => navigate(`/${lang}/about-us#why-choose-us`)}
                            label={readMoreLabel}
                        />
                    </div>
                )}
            </div>
        </section>
    );
};

export default WhyChooseUs;
